"use client";

import { c as _c } from "react/compiler-runtime";
import { EnhancedJsonViewWithCopy } from "@/components/ui/enhanced-json-view";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { useSlackChannels } from "@/app/(v2)/workflows/_hooks/useSlackChannels";
import { resolveSlackConversationLabel } from "@/app/(v2)/workflows/_components/form-fields/slackChannels";
import { formatRRule, formatDelayTime, formatTokenAmount } from "../../utils/formatters";
import { ChainBadge } from "../ChainBadge";
import { extractChainIdsFromObject, findMatchingOrganizationWallet, InlineDetectedValue, isChainIdKey, isEthAddress, type InlineTokenData } from "../InlineAddressLink";
import { VariablePill } from "../VariablePill";
interface GenericPayloadProps {
  payload: Record<string, unknown>;
  /** Connector info for the action — used to resolve Slack channel names */
  connector?: {
    id?: string;
    type: string;
  };
}

/**
 * Highlights template variables ({{...}} or @{{...}}) in a string using VariablePill
 */
function highlightTemplateVariables(text: string): React.ReactNode {
  const parts = text.split(/(@?\{\{[^}]+\}\})/g);
  if (parts.length === 1) {
    return text;
  }
  return parts.map((part, i) => {
    if (/^@?\{\{[^}]+\}\}$/.test(part)) {
      return <VariablePill key={i} variable={part} />;
    }
    return part ? <span key={i}>{part}</span> : null;
  });
}

/**
 * Process React children to highlight {{variable}} patterns
 */
function processChildrenForVariables(children: React.ReactNode): React.ReactNode {
  if (typeof children === "string") {
    return highlightTemplateVariables(children);
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => {
      if (typeof child === "string") {
        return <span key={i}>{highlightTemplateVariables(child)}</span>;
      }
      return child;
    });
  }
  return children;
}

/**
 * Markdown renderer for templates that highlights {{variables}}
 * Converts single newlines to line breaks (like GitHub-flavored markdown)
 */
function TemplateMarkdownPreview(t0) {
  const $ = _c(6);
  const {
    content
  } = t0;
  let t1;
  if ($[0] !== content) {
    t1 = content.replace(/(?<!\n)\n(?!\n)/g, "  \n");
    $[0] = content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const processedContent = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [remarkGfm];
    t3 = {
      p: _temp,
      strong: _temp2,
      em: _temp3,
      li: _temp4,
      ul: _temp5,
      ol: _temp6,
      a: _temp7,
      code: _temp8,
      h1: _temp9,
      h2: _temp10,
      h3: _temp11
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== processedContent) {
    t4 = <div className="template-preview text-ds-text-secondary text-xs"><ReactMarkdown remarkPlugins={t2} components={t3}>{processedContent}</ReactMarkdown></div>;
    $[4] = processedContent;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

/**
 * GenericPayload - Component for rendering generic workflow step payloads
 * Handles special formatting for RRULE recurrence rules, delays, and chain IDs
 */
function _temp11(t0) {
  const {
    children: children_9
  } = t0;
  return <div className="mb-0.5 font-semibold">{processChildrenForVariables(children_9)}</div>;
}
function _temp10(t0) {
  const {
    children: children_8
  } = t0;
  return <div className="mb-1 font-bold">{processChildrenForVariables(children_8)}</div>;
}
function _temp9(t0) {
  const {
    children: children_7
  } = t0;
  return <div className="mb-1 font-bold">{processChildrenForVariables(children_7)}</div>;
}
function _temp8(t0) {
  const {
    children: children_6
  } = t0;
  return <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 text-3xs rounded px-1 py-0.5 font-mono">{processChildrenForVariables(children_6)}</code>;
}
function _temp7(t0) {
  const {
    children: children_5
  } = t0;
  return <span className="text-blue-600 underline dark:text-blue-400">{processChildrenForVariables(children_5)}</span>;
}
function _temp6(t0) {
  const {
    children: children_4
  } = t0;
  return <ol className="list-decimal space-y-0.5 pl-4">{children_4}</ol>;
}
function _temp5(t0) {
  const {
    children: children_3
  } = t0;
  return <ul className="list-disc space-y-0.5 pl-4">{children_3}</ul>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <li className="leading-relaxed">{processChildrenForVariables(children_2)}</li>;
}
function _temp3(t0) {
  const {
    children: children_1
  } = t0;
  return <em className="italic">{processChildrenForVariables(children_1)}</em>;
}
function _temp2(t0) {
  const {
    children: children_0
  } = t0;
  return <strong className="font-semibold">{processChildrenForVariables(children_0)}</strong>;
}
function _temp(t0) {
  const {
    children
  } = t0;
  return <p className="mb-1 leading-relaxed last:mb-0">{processChildrenForVariables(children)}</p>;
}
export function GenericPayload({
  payload,
  connector
}: GenericPayloadProps) {
  const chainIds = useMemo(() => extractChainIdsFromObject(payload), [payload]);
  const hasSlackChannelField = typeof payload.channel === "string" || typeof payload.conversation === "string";
  const {
    slackChannelsMap,
    isSlackConnector
  } = useSlackChannels(connector?.id, connector?.type, hasSlackChannelField);
  const {
    data: chainInfoMap
  } = useChainInfoMap(chainIds);
  const {
    selectedOrgId
  } = useOrganizations();
  const {
    wallets
  } = useOrganizationWallets(selectedOrgId ?? undefined);

  // Extract token addresses and chainId for token lookups
  const chainId = chainIds[0] as number | undefined;
  const tokenAddress = payload.tokenAddress as string | undefined;
  const tokenInAddress = payload.tokenIn as string | undefined;
  const tokenOutAddress = payload.tokenOut as string | undefined;

  // Fetch token info for tokenAddress, tokenIn, and tokenOut
  const shouldFetchToken = !!(tokenAddress && chainId && typeof tokenAddress === "string" && tokenAddress.startsWith("0x"));
  const shouldFetchTokenIn = !!(tokenInAddress && chainId && typeof tokenInAddress === "string" && tokenInAddress.startsWith("0x"));
  const shouldFetchTokenOut = !!(tokenOutAddress && chainId && typeof tokenOutAddress === "string" && tokenOutAddress.startsWith("0x"));
  const {
    data: simTokenData
  } = useSimTokenInfo(tokenAddress, chainId, shouldFetchToken);
  const {
    data: simTokenInData
  } = useSimTokenInfo(tokenInAddress, chainId, shouldFetchTokenIn);
  const {
    data: simTokenOutData
  } = useSimTokenInfo(tokenOutAddress, chainId, shouldFetchTokenOut);
  const tokenInfo = useMemo(() => {
    const token = simTokenData?.tokens?.[0];
    if (!token) return null;
    return {
      symbol: token.symbol,
      name: token.name || token.symbol,
      decimals: token.decimals,
      logoUrl: token.logo || token.token_metadata?.logo,
      address: simTokenData.contract_address,
      chainId: token.chain_id
    };
  }, [simTokenData]);
  const tokenInInfo = useMemo(() => {
    const token_0 = simTokenInData?.tokens?.[0];
    if (!token_0) return null;
    return {
      symbol: token_0.symbol,
      name: token_0.name || token_0.symbol,
      decimals: token_0.decimals,
      logoUrl: token_0.logo || token_0.token_metadata?.logo,
      address: simTokenInData.contract_address,
      chainId: token_0.chain_id
    };
  }, [simTokenInData]);
  const tokenOutInfo = useMemo(() => {
    const token_1 = simTokenOutData?.tokens?.[0];
    if (!token_1) return null;
    return {
      symbol: token_1.symbol,
      name: token_1.name || token_1.symbol,
      decimals: token_1.decimals,
      logoUrl: token_1.logo || token_1.token_metadata?.logo,
      address: simTokenOutData.contract_address,
      chainId: token_1.chain_id
    };
  }, [simTokenOutData]);
  const tokensMap = useMemo(() => {
    const entries: Array<[string, InlineTokenData]> = [tokenInfo, tokenInInfo, tokenOutInfo].filter((token_2): token_2 is NonNullable<typeof token_2> => Boolean(token_2)).map(token_3 => [token_3.address.toLowerCase(), {
      address: token_3.address,
      symbol: token_3.symbol,
      name: token_3.name,
      chainId: token_3.chainId,
      logoUrl: token_3.logoUrl
    }]);
    return entries.length > 0 ? new Map(entries) : undefined;
  }, [tokenInfo, tokenInInfo, tokenOutInfo]);
  if (!payload || Object.keys(payload).length === 0) {
    return null;
  }
  return <div className="border-ds-border-default space-y-2.5 border-t pt-3">
      {Object.entries(payload).map(([key, value]) => {
      let label = key.replace(/([a-z\d])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z]{2,})/g, "$1 $2").replace(/^./, str => str.toUpperCase()).trim();
      const isObject = typeof value === "object" && value !== null;
      const displayValue = isObject ? null : String(value);
      const isRRule = key === "rrule" && typeof value === "string";
      const isDelay = (key.toLowerCase().includes("delay") || key.toLowerCase().includes("ms")) && typeof value === "number";
      const isChainId = isChainIdKey(key) && typeof value === "number";
      const isChainIdsArray = key === "chainIds" && Array.isArray(value);

      // Special label formatting for chain IDs
      if (isChainId) {
        label = "Chain";
      }
      if (isChainIdsArray) {
        const normalizedChainIds = (value as unknown[]).map(chainId_0 => typeof chainId_0 === "number" ? chainId_0 : Number(chainId_0)).filter(chainId_1 => Number.isFinite(chainId_1) && chainId_1 > 0);
        return <div key={key} className="flex items-start gap-2 text-xs">
              <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">
                {normalizedChainIds.length === 1 ? "Chain:" : "Chains:"}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {normalizedChainIds.map(chainId_2 => {
              const chain = chainInfoMap.get(chainId_2);
              return chain ? <ChainBadge key={chainId_2} chain={chain} size="sm" variant="summary" /> : <span key={chainId_2} className="text-ds-text-primary font-mono">
                      {chainId_2}
                    </span>;
            })}
              </div>
            </div>;
      }

      // Special handling for chain IDs - show as badge with icon
      if (isChainId) {
        const chain_0 = chainInfoMap.get(value as number);
        return <div key={key} className="flex items-center gap-2 text-xs">
              <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">{label}:</span>
              {chain_0 ? <ChainBadge chain={chain_0} size="sm" variant="summary" /> : <span className="text-ds-text-primary font-mono">{displayValue}</span>}
            </div>;
      }
      const matchingWallet = typeof value === "string" ? findMatchingOrganizationWallet(value, wallets) : undefined;
      if (typeof value === "string" && (Boolean(matchingWallet) || isEthAddress(value))) {
        return <div key={key} className="flex items-center gap-2 text-xs">
              <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">{label}:</span>
              <InlineDetectedValue value={value} keyName={key} parentObject={payload} wallets={wallets} tokens={tokensMap} variant="summary" />
            </div>;
      }

      // Special handling for amount-like fields - format with token decimals
      if ((key === "amount" || key === "amountIn") && (typeof value === "string" || typeof value === "number") && !String(value).includes("{{")) {
        const decimals = key === "amountIn" && tokenInInfo ? tokenInInfo.decimals : tokenInfo ? tokenInfo.decimals : undefined;
        if (decimals !== undefined) {
          return <div key={key} className="flex items-start gap-2 text-xs">
                <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">
                  {label}:
                </span>
                <span className="text-ds-text-primary font-mono">
                  {formatTokenAmount(value as string | number, decimals)}
                </span>
              </div>;
        }
      }

      // Special handling for object values - show in emboss-inner container with enhanced JSON view
      if (isObject) {
        return <div key={key} className="text-xs">
              <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">{label}:</span>
              <div className="emboss-inner !bg-ds-neutral-100 dark:!bg-ds-neutral-800 mt-1 rounded-lg p-3 font-mono text-xs">
                <EnhancedJsonViewWithCopy value={value} defaultExpanded={true} showExpandAll={false} />
              </div>
            </div>;
      }

      // Special handling for template/message fields - render markdown with variable highlighting
      if ((key === "template" || key === "message") && typeof value === "string") {
        return <div key={key} className="space-y-1 text-xs">
              <span className="text-ds-text-muted dark:text-ds-text-muted font-medium capitalize">{label}:</span>
              <TemplateMarkdownPreview content={value} />
            </div>;
      }
      if ((key === "channel" || key === "conversation") && typeof value === "string" && isSlackConnector) {
        const resolved = resolveSlackConversationLabel(value, slackChannelsMap);
        return <div key={key} className="flex items-start gap-2 text-xs">
              <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">Channel:</span>
              <span className="text-ds-text-primary font-mono">{resolved || value}</span>
            </div>;
      }

      // Special handling for RRULE and delays
      let extraInfo = "";
      if (isRRule) {
        extraInfo = ` (${formatRRule(value as string)})`;
      } else if (isDelay) {
        extraInfo = ` (${formatDelayTime(value as number)})`;
      }
      return <div key={key} className="flex items-start gap-2 text-xs">
            <span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">{label}:</span>
            <span className="text-ds-text-primary font-mono">
              {displayValue ? highlightTemplateVariables(displayValue) : displayValue}
              {extraInfo && <span className="text-ds-text-muted ml-1">{extraInfo}</span>}
            </span>
          </div>;
    })}
    </div>;
}